import { Component, OnDestroy } from '@angular/core';
import { EnvironmentService } from '@galaxy/core';
import { ProductAnalyticsService } from '@vendasta/product-analytics';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { AppConfigService } from './core/appconfig/app-config.service';
import { getCookie } from './core/cookie';
import { getImageSrc } from './core/image-utilities';
import { SelectedPartnerService } from './selected-partner.service';
import { FeatureFlagService } from './core/feature-flag.service';

declare const deployment: string; // Stamped down by vStatic in the index.html

export interface ProductNavItem {
  icon?: string;
  label: string;
  url: string;
  children?: ProductNavItem[];
  isIconSvg?: boolean;
  showUpgradeChip?: boolean;
}

export interface FeatureFlagStatusInterface {
  [status: string]: boolean;
}

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false,
})
export class AppComponent implements OnDestroy {
  title = 'wsp-admin-center-client';
  logoUrl: string;

  private partnerId: string;

  private subscriptions: Subscription[] = [];

  navItems$$: BehaviorSubject<ProductNavItem[]> = new BehaviorSubject<ProductNavItem[]>(null);
  navItems$: Observable<ProductNavItem[]> = this.navItems$$.asObservable();

  constructor(
    private env: EnvironmentService,
    private appConfigService: AppConfigService,
    private selectedPartnerService: SelectedPartnerService,
    private snowplowTracker: ProductAnalyticsService,
    private featureFlagService: FeatureFlagService,
  ) {
    this.logoUrl = getImageSrc('website-pro.svg');

    this.subscriptions.push(
      this.selectedPartnerService
        .getPartnerId()
        .pipe(filter((partnerId: string) => !!partnerId))
        .subscribe((partnerId: string) => {
          this.partnerId = partnerId;
          this.getFeatureFlags();
        }),
    );

    // Prevents capturing events on the local environment
    if (typeof deployment !== 'undefined') {
      this.snowplowTracker.initialize({
        environment: this.env.getEnvironment(),
        projectUUID: '4b55ce87-cfa4-4c68-9b35-00633404f576',
        postHogID: 'phc_bh3h18PyXSKq6kJ6xOvge53cGMhfcYCrOdhSBN6O3ak',
        projectName: 'wsp-admin-center-client',
        // this falls back to user ID if the unified one is not available
        partner: {
          pid: getCookie('partner_id'),
          pidChanges$: this.selectedPartnerService.getPartnerId(),
        },
        businessID: getCookie('account_id'),
      });
    }
  }

  getFeatureFlags(): void {
    this.featureFlagService.checkFeatureFlag(this.partnerId, '', 'admin_center_uptime_report').subscribe((isUptime) => {
      if (isUptime) {
        this.navItems$$.next(this.partnerTabsWithUptime());
      } else {
        this.navItems$$.next(this.partnerTabsWithoutUptime());
      }
    });
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }

  partnerTabsWithUptime(): ProductNavItem[] {
    const tabs = [
      {
        label: 'NAVIGATION.TABS.DASHBOARD',
        url: `/partner/${this.partnerId}/dashboard`,
        icon: 'dashboard',
        children: [
          {
            label: 'NAVIGATION.TABS.UPTIME',
            url: `/partner/${this.partnerId}/uptime`,
            icon: 'dashboard',
            children: null,
          },
          {
            label: 'NAVIGATION.TABS.UPDATE',
            url: `/partner/${this.partnerId}/dashboard`,
            icon: 'dashboard',
            children: null,
          },
          // {
          //   label: 'NAVIGATION.TABS.VULNERABILITIES',
          //   url: `/partner/${this.partnerId}/vulnerabilities`,
          //   icon: 'bug_report',
          //   children: null,
          // },
        ],
      },
      {
        label: 'NAVIGATION.TABS.PRODUCT_SETTINGS',
        url: `/partner/${this.partnerId}/settings`,
        icon: 'settings',
        children: null,
      },
      {
        label: 'NAVIGATION.TABS.TEMPLATES',
        url: `/partner/${this.partnerId}/templates`,
        icon: 'dashboard_customize',
        children: null,
      },
    ];

    return tabs;
  }

  partnerTabsWithoutUptime(): ProductNavItem[] {
    const tabs = [
      {
        label: 'NAVIGATION.TABS.DASHBOARD',
        url: `/partner/${this.partnerId}/dashboard`,
        icon: 'dashboard',
        children: null,
      },
      {
        label: 'NAVIGATION.TABS.PRODUCT_SETTINGS',
        url: `/partner/${this.partnerId}/settings`,
        icon: 'settings',
        children: null,
      },
      {
        label: 'NAVIGATION.TABS.TEMPLATES',
        url: `/partner/${this.partnerId}/templates`,
        icon: 'dashboard_customize',
        children: null,
      },
    ];

    return tabs;
  }
}
